import type {
    ResourcePrice} from "@@/shared/acquire/resource-price/resource-price";
import {
    resourcePriceZodSchema,
} from "@@/shared/acquire/resource-price/resource-price";
import type {
    BookableOptionId} from "@@/shared/acquire/resource-reference-options/bookable-option-id";
import {
    bookableOptionIdZodSchema,
} from "@@/shared/acquire/resource-reference-options/bookable-option-id";
import type {
    ResourceReferenceId} from "@@/shared/acquire/resource-references/resource-reference-id";
import {
    resourceReferenceIdZodSchema,
} from "@@/shared/acquire/resource-references/resource-reference-id";
import type {
    BookableSessionSeriesId} from "@@/shared/acquire/sessions/bookable-session-series-id";
import {
    bookableSessionSeriesIdZodSchema,
} from "@@/shared/acquire/sessions/bookable-session-series-id";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import { productIdZodSchema } from "@@/shared/products/product-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { ProductCommandCommon } from "./product-commands-common";

type ReferencePriceOverride = [
    id: ResourceReferenceId,
    option: BookableOptionId,
    price: ResourcePrice,
];

type BookableSeriesChangePriceCommand = ProductCommandCommon & {
    readonly _id: string;
    readonly _type: "BOOKABLE_SERIES_CHANGE_PRICE_COMMAND";
    readonly seriesId: BookableSessionSeriesId;
    readonly newMapping: ReferencePriceOverride[];
};

type CreateParams = Except<
    ProductCommandCommon & BookableSeriesChangePriceCommand,
    "_id" | "_type" | "_createdAt"
>;
const bookableSeriesPriceChangeCommandFactory = (params: CreateParams) => {
    const editCommand: BookableSeriesChangePriceCommand = {
        ...params,
        _id: generateId({
            prefix: "bookable_change_duration_command__",
        }),
        _type: "BOOKABLE_SERIES_CHANGE_PRICE_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return editCommand;
};

const bookableSeriesChangePriceCommandSchema: z.ZodSchema<BookableSeriesChangePriceCommand> =
    z.object({
        _id: idZodSchema,
        _type: z.literal("BOOKABLE_SERIES_CHANGE_PRICE_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        providerId: providerIdZodSchema,
        productId: productIdZodSchema,
        seriesId: bookableSessionSeriesIdZodSchema,
        newMapping: z.array(
            z.tuple([
                resourceReferenceIdZodSchema,
                bookableOptionIdZodSchema,
                resourcePriceZodSchema,
            ]),
        ),
    });

export {
    bookableSeriesChangePriceCommandSchema,
    bookableSeriesPriceChangeCommandFactory,
};
export type {
    BookableSeriesChangePriceCommand,
    CreateParams as CreateBookableSeriesChangePriceCommandParams,
    ReferencePriceOverride,
};
